import React, { useEffect, useState } from 'react'
import { Box, ButtonBase, Checkbox, InputAdornment, Skeleton, TextField, Typography } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { Search as SearchIcon } from '@material-ui/icons'
import { useDebounce } from 'use-debounce'
import FileNameText from 'src/components/FileNameText'
import { Muted, Segmented, TextLink } from '../components/ui'
import { shortDay } from '../model/candidates'
import { COHORT_PAGE_SIZE, useCohortCandidates } from './useFinishedAnalyses'
import { cohortRowDisabledReason } from './model'

// The samples area of a CNV cohort: finished exome analyses on the chosen kit, 50 a
// page, searched by sample or file name. The selection is kept across pages and
// searches; a second analysis of a sample already picked, and a 51st, are blocked.

const FIELD_OPTIONS = [
  { value: 'sample', label: 'Sample' },
  { value: 'file', label: 'File' },
]

const GRID = '36px minmax(0, 1fr) minmax(0, 1.4fr) 84px 150px'

const dayOf = (value) => {
  const t = new Date(value).getTime()
  return Number.isFinite(t) ? shortDay(t) : ''
}

const CohortRow = ({ row, checked, reason, onToggle }) => {
  const theme = useTheme()
  const disabled = Boolean(reason)
  return (
    <ButtonBase
      component="div"
      role="checkbox"
      aria-checked={checked}
      aria-disabled={disabled}
      aria-label={row.key}
      onClick={() => !disabled && onToggle(row)}
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: GRID,
        alignItems: 'center',
        columnGap: 2,
        minHeight: 48,
        px: 1,
        py: 0.5,
        borderRadius: `${theme.shape.borderRadius}px`,
        textAlign: 'left',
        cursor: disabled ? 'default' : 'pointer',
        '&:hover': { backgroundColor: disabled ? 'transparent' : 'action.hover' },
      }}
    >
      <Checkbox
        size="small"
        checked={checked}
        disabled={disabled}
        tabIndex={-1}
        sx={{ p: 0.5 }}
        inputProps={{ 'aria-hidden': true }}
      />
      <Box sx={{ minWidth: 0, opacity: disabled ? 0.6 : 1 }}>
        <FileNameText name={row.key} variant="plain" maxLines={1} sx={{ fontSize: 14, fontWeight: 500 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {row.files.map((name) => (
          <Typography key={name} sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.45 }} noWrap title={name}>
            {name}
          </Typography>
        ))}
      </Box>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }} noWrap>
        {dayOf(row.createdAt)}
      </Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'right' }} noWrap>
        {reason}
      </Typography>
    </ButtonBase>
  )
}

/**
 * @param cohort    groupState.cohort ({ kitId, members })
 * @param kitId     the workflow's kit (BedFile name); the list is empty without one
 * @param onToggle  (row) => void
 * @param onClear   () => void
 */
const CohortArea = ({ cohort, kitId, kitName, onToggle, onClear }) => {
  const [search, setSearch] = useState('')
  const [field, setField] = useState('sample')
  const [page, setPage] = useState(0)
  const [debounced] = useDebounce(search, 300)

  // A new kit or search starts from the first page.
  useEffect(() => setPage(0), [kitId, debounced, field])

  const list = useCohortCandidates({ kitId, search: debounced, field, page, enabled: Boolean(kitId) })
  const members = cohort.kitId === kitId ? cohort.members : []
  const selectedIds = new Set(members.map((m) => m.pairId))

  if (!kitId) {
    return (
      <Box sx={{ maxWidth: 880, px: 1, py: 2 }}>
        <Muted>Choose a capture kit to list finished exome analyses on it.</Muted>
      </Box>
    )
  }

  const from = list.total ? page * COHORT_PAGE_SIZE + 1 : 0
  const to = Math.min(page * COHORT_PAGE_SIZE + Math.max(list.rows.length, 0), list.total || 0)

  return (
    <Box sx={{ maxWidth: 960 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', px: 1, pb: 1.5 }}>
        <TextField
          size="small"
          placeholder={field === 'file' ? 'Search file names' : 'Search samples'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputProps={{ 'aria-label': 'Search finished analyses' }}
          sx={{ width: 240, '& .MuiInputBase-root': { fontSize: 14 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        <Segmented
          ariaLabel="Search by"
          value={field}
          options={FIELD_OPTIONS}
          onChange={setField}
          fullWidth={false}
          size="small"
        />
        <Box sx={{ flex: 1 }} />
        <Muted component="span">{members.length} selected</Muted>
        {members.length > 0 && (
          <TextLink tone="muted" onClick={onClear}>
            Clear
          </TextLink>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: GRID,
          columnGap: 2,
          px: 1,
          pb: 0.5,
          '& > *': { fontSize: 12.5, color: 'text.secondary', fontWeight: 500 },
        }}
      >
        <span />
        <Typography>Sample</Typography>
        <Typography>Files</Typography>
        <Typography>Finished</Typography>
        <span />
      </Box>

      {list.isLoading ? (
        Array.from({ length: 5 }).map((_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Skeleton key={i} variant="text" height={48} sx={{ mx: 1 }} />
        ))
      ) : list.isError ? (
        <Muted sx={{ px: 1, py: 3 }}>Finished analyses could not be loaded.</Muted>
      ) : list.rows.length === 0 ? (
        <Muted sx={{ px: 1, py: 3 }}>
          {debounced.trim()
            ? 'No finished analyses match.'
            : `No finished exome analyses on ${kitName || 'this kit'} yet.`}
        </Muted>
      ) : (
        list.rows.map((row) => (
          <CohortRow
            key={row.pairId}
            row={row}
            checked={selectedIds.has(row.pairId)}
            reason={cohortRowDisabledReason({ kitId, members }, row)}
            onToggle={onToggle}
          />
        ))
      )}

      {list.total > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, px: 1, pt: 1.5 }}>
          <Muted component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {from} to {to} of {list.total}
          </Muted>
          <TextLink disabled={!list.hasPrevious} onClick={() => setPage((p) => Math.max(p - 1, 0))}>
            Previous
          </TextLink>
          <TextLink disabled={!list.hasNext} onClick={() => setPage((p) => p + 1)}>
            Next
          </TextLink>
        </Box>
      )}
    </Box>
  )
}

export default CohortArea
